import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  Tabs,
  Tab,
  Stack,
  Snackbar,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from './design-system';
import { EmailField } from './forms';
import {
  Edit,
  Save,
  Cancel,
  Email,
} from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../services/ProviderTenantsService';
import { providerSupportService } from '../services/ProviderSupportService';
import { tenantUserService, type TenantUserDetail } from '../services/TenantUserService';
import { useFormatting } from '../contexts/FormattingContext';
import { validateUpdateTenant, validateCreateTenantUser, formatValidationErrors } from '../utils/validation';
import { type CreateTenantUserRequest } from '../../../shared/src/schemas';
import * as dt from '../../../shared/src/data.types';
import SsoConfigurationPanel from './admin/SsoConfigurationPanel';
import { EnhancedTable } from './ui/EnhancedTable';
import SensitiveActionDialog from './dialogs/SensitiveActionDialog';

interface TenantDetailsProps {
  open: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onTenantUpdated: (tenant: Tenant) => void;
  readOnly?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TenantDetails: React.FC<TenantDetailsProps> = ({
  open,
  tenant,
  onClose,
  onTenantUpdated,
  readOnly = false,
}) => {
  const { formatDate } = useFormatting();
  
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUserDetail[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState<dt.IntegerId | null>(null);
  const [resendInvitationUser, setResendInvitationUser] = useState<TenantUserDetail | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
  });

  // Create user form state
  const [userFormData, setUserFormData] = useState<CreateTenantUserRequest>({
    email: '' as dt.Email,
    firstName: '' as dt.FirstName,
    lastName: '' as dt.LastName,
    role: dt.ALL_USER_ROLES.tenant_admin,
  });

  const loadTenantUsers = useCallback(async () => {
    if (!tenant) return;
    try {
      setLoading(true);
      const users = readOnly
        ? await providerSupportService.getTenantUsers(tenant.id)
        : await providerTenantsService.getTenantUsers(tenant.id);
      setTenantUsers(users as unknown as TenantUserDetail[]);
    } catch (err) {
      console.error('Failed to load tenant users:', err);
      setError('Failed to load tenant users');
    } finally {
      setLoading(false);
    }
  }, [tenant, readOnly]);

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        subdomain: tenant.subdomain,
      });
      loadTenantUsers();
    }
  }, [tenant, loadTenantUsers]);

  const handleSave = async () => {
    if (!tenant) return;
    try {
      setLoading(true);
      setError(null);

      // Validate form data
      const validation = validateUpdateTenant(formData);
      if (!validation.success) {
        setError(`Validation failed:\n${formatValidationErrors(validation.errors || [])}`);
        return;
      }

      const updatedTenant = await providerTenantsService.updateTenant(tenant.id, validation.data!);
      onTenantUpdated(updatedTenant);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!tenant) return;
    try {
      setLoading(true);
      setError(null);

      // Validate user form data
      const validation = validateCreateTenantUser(userFormData);
      if (!validation.success) {
        setError(`Validation failed:\n${formatValidationErrors(validation.errors || [])}`);
        return;
      }

      const newUser = await providerTenantsService.createTenantUser(tenant.id, validation.data!);
      setTenantUsers([newUser as unknown as TenantUserDetail, ...tenantUsers]);
      setUserFormData({
        email: '' as dt.Email,
        firstName: '' as dt.FirstName,
        lastName: '' as dt.LastName,
        role: dt.ALL_USER_ROLES.tenant_admin
      });
      setShowCreateUser(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };


  const handleResendInvitationClick = (user: TenantUserDetail) => {
    setResendInvitationUser(user);
  };

  const handleResendInvitationConfirm = async (stepUpToken: string) => {
    if (!resendInvitationUser) {
      return;
    }

    try {
      setResendingInvitation(resendInvitationUser.id);
      setError(null);
      await tenantUserService.resendTenantUserInvitation(resendInvitationUser.id, stepUpToken);
      setShowSuccessSnackbar(true);
    } finally {
      setResendingInvitation(null);
    }
  };


  if (!tenant) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isEditing ? 'Edit Tenant' : 'Tenant Details'} - {tenant.name}
          </Typography>
          <Box>
            {!readOnly && !isEditing && (
              <AppIconButton onClick={() => setIsEditing(true)} size="small">
                <Edit />
              </AppIconButton>
            )}
            {!readOnly && isEditing && (
              <>
                <AppIconButton onClick={handleSave} size="small" disabled={loading}>
                  <Save />
                </AppIconButton>
                <AppIconButton onClick={() => setIsEditing(false)} size="small">
                  <Cancel />
                </AppIconButton>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Details" />
            <Tab label="Users" />
            <Tab label="SSO Configuration" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={2}>
            {/* @ts-expect-error - MUI Grid v6 typing issue with item prop */}
            <Grid item xs={12} sm={6}>
              <AppTextField
                fullWidth
                label="Tenant Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                variant={isEditing ? 'outlined' : 'filled'}
              />
            </Grid>
            {/* @ts-expect-error - MUI Grid v6 typing issue with item prop */}
            <Grid item xs={12} sm={6}>
              <AppTextField
                fullWidth
                label="Subdomain"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                disabled={!isEditing}
                variant={isEditing ? 'outlined' : 'filled'}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Tenant Users</Typography>
          </Box>

          <EnhancedTable<TenantUserDetail>
            data={tenantUsers}
            totalCount={tenantUsers.length}
            columns={[
              {
                field: 'firstName',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
                renderCell: (row: TenantUserDetail) => `${row.firstName} ${row.lastName}`,
              },
              {
                field: 'email',
                headerName: 'Email',
                flex: 1.2,
                minWidth: 200,
              },
              {
                field: 'role',
                headerName: 'Role',
                flex: 0.8,
                minWidth: 120,
                renderCell: (row: TenantUserDetail) => dt.getRoleDisplayName(row.role),
              },
              {
                field: 'isActive',
                headerName: 'Status',
                flex: 0.6,
                minWidth: 100,
                renderCell: (row: TenantUserDetail) => (
                  <Chip
                    label={row.isActive ? 'Active' : 'Inactive'}
                    color={row.isActive ? 'success' : 'default'}
                    size="small"
                  />
                ),
              },
              {
                field: 'lastLoginAt',
                headerName: 'Last Login',
                flex: 1,
                minWidth: 150,
                renderCell: (row: TenantUserDetail) =>
                  row.lastLoginAt ? formatDate(new Date(row.lastLoginAt)) : 'Never',
              },
            ]}
            actions={[
              {
                label: 'Resend Invitation',
                description:
                  'Generate a new temporary password and resend the welcome email (for users who have not finished onboarding)',
                icon: <Email />,
                onClick: (row: TenantUserDetail) => handleResendInvitationClick(row),
                disabled: (row: TenantUserDetail) => !!row.lastLoginAt || resendingInvitation === row.id,
                visible: (row: TenantUserDetail) => !row.lastLoginAt,
              },
            ]}
            getRowId={(row) => row.email}
            searchable
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onAdd={readOnly ? undefined : () => setShowCreateUser(true)}
            addLabel="Add user"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <SsoConfigurationPanel
            tenant={tenant}
            onSsoUpdated={(updated) => onTenantUpdated(updated as Tenant)}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <AppButton onClick={onClose}>Close</AppButton>
      </DialogActions>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onClose={() => setShowCreateUser(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Name Fields Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <AppTextField
                fullWidth
                label="First Name"
                variant="outlined"
                value={userFormData.firstName}
                onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value as dt.FirstName})}
              />
              <AppTextField
                fullWidth
                label="Last Name"
                variant="outlined"
                value={userFormData.lastName}
                onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value as dt.LastName })}
              />
            </Stack>

            {/* Email Field */}
            <EmailField
              value={userFormData.email}
              onChange={(value) => setUserFormData({ ...userFormData, email: value as dt.Email })}
              required
            />

            {/* Role and Password Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="tenant_admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <AppTextField
                fullWidth
                label="Password (optional)"
                variant="outlined"
                type="password"
                value={undefined}
                onChange={() => setUserFormData({ ...userFormData })}
                helperText="Leave blank to generate automatically"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setShowCreateUser(false)}>Cancel</AppButton>
          <AppButton onClick={handleCreateUser} variant="contained" disabled={loading}>
            Create User
          </AppButton>
        </DialogActions>
      </Dialog>

      <SensitiveActionDialog
        open={resendInvitationUser !== null}
        action="resend-invitation"
        target={
          resendInvitationUser
            ? {
                id: String(resendInvitationUser.id),
                email: resendInvitationUser.email,
                displayName: `${resendInvitationUser.firstName} ${resendInvitationUser.lastName}`,
              }
            : null
        }
        onClose={() => setResendInvitationUser(null)}
        onConfirm={handleResendInvitationConfirm}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success">
          Invitation email sent successfully!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default TenantDetails;
